import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { File } from '@actual-app/core/types/file';

import { compartidoCon } from '#components/manager/puerto/datos';

import type { Objetivo } from './objetivos';
import { esObjetivoConFecha, LARGO_CARTA, LARGO_FIRMA } from './recuerdos';
import type { Recuerdo } from './recuerdos';

/**
 * Pantallas de las travesías (zarpar, tipo de categoría y cierre): lógica
 * pura, sin React. Los datos viven en `recuerdos.ts`; aquí solo se decide
 * qué enseñar y qué hacer.
 */

/** Tipo de una categoría en la hoja de editar: sale de lo que ya hay. */
export type TipoCategoria = 'normal' | 'fijo' | 'travesia';

/**
 * Pago fijo si tiene el interruptor (pref `pagos-fijos`), aunque su objetivo
 * tenga fecha (un seguro anual); travesía si tiene objetivo con fecha
 * (una vez o cada año); si no, normal.
 */
export function tipoDeCategoria({
  objetivo,
  esFijo,
}: {
  objetivo: Objetivo | null | 'otro' | undefined;
  esFijo: boolean;
}): TipoCategoria {
  if (esFijo) {
    return 'fijo';
  }
  return esObjetivoConFecha(objetivo) ? 'travesia' : 'normal';
}

/** Lo que hay que hacer al cambiar el tipo en la hoja de editar. */
export type PlanCambioTipo = {
  /** Valor nuevo del interruptor «Pago fijo», o null si no cambia. */
  fijo: boolean | null;
  /** Abrir «Zarpar» (crear la meta con fecha y su carta). */
  zarpar: boolean;
  /** Abrir «Cerrar la travesía» (deja de ser travesía). */
  cerrar: boolean;
};

export function planCambioTipo(
  antes: TipoCategoria,
  despues: TipoCategoria,
): PlanCambioTipo {
  const nada: PlanCambioTipo = { fijo: null, zarpar: false, cerrar: false };
  if (antes === despues) {
    return nada;
  }
  switch (despues) {
    case 'fijo':
      // Un pago fijo puede tener fecha (seguro anual): la meta se queda.
      return { ...nada, fijo: true };
    case 'travesia':
      return { ...nada, fijo: antes === 'fijo' ? false : null, zarpar: true };
    default:
      return antes === 'fijo'
        ? { ...nada, fijo: false }
        : { ...nada, cerrar: true };
  }
}

const MARCA_FECHA = /#objetivo\s+fecha\s+\d{4}-\d{2}-\d{2}/i;

/** Las líneas de objetivo que se van a guardar son de una meta con fecha. */
export function lineasConFecha(lineas: readonly string[]): boolean {
  return lineas.some(l => MARCA_FECHA.test(l));
}

/** Travesías navegando (sin llegar) de una categoría. */
export function navegandoDe(
  recuerdos: readonly Recuerdo[],
  categoriaId: string,
): Recuerdo[] {
  return recuerdos.filter(
    r =>
      r.base.categoriaId === categoriaId &&
      r.base.estado === 'navegando' &&
      !r.llegada,
  );
}

/**
 * La travesía navegando de la categoría: la del ciclo actual (`rid`) si la
 * hay; si no, la que zarpó antes.
 */
export function travesiaActual(
  recuerdos: readonly Recuerdo[],
  categoriaId: string,
  rid: string | null,
): Recuerdo | null {
  const vivas = navegandoDe(recuerdos, categoriaId);
  return (
    vivas.find(r => r.base.rid === rid) ??
    [...vivas].sort((a, b) => (a.base.zarpoEn < b.base.zarpoEn ? -1 : 1))[0] ??
    null
  );
}

/**
 * «Cambiar de rumbo»: otras travesías navegando, de otras categorías (en la
 * misma no hay dinero que mover).
 */
export function destinosRumbo(
  recuerdos: readonly Recuerdo[],
  origen: Recuerdo,
): Recuerdo[] {
  return recuerdos.filter(
    r =>
      r.base.rid !== origen.base.rid &&
      r.base.categoriaId !== origen.base.categoriaId &&
      r.base.estado === 'navegando' &&
      !r.llegada,
  );
}

/** Traspaso de lo ahorrado al cambiar de rumbo (null si no hay nada). */
export function movimientoRumbo({
  saldo,
  desde,
  hacia,
}: {
  saldo: IntegerAmount | null | undefined;
  desde: string;
  hacia: string;
}): { amount: IntegerAmount; from: string; to: string } | null {
  if (!saldo || saldo <= 0 || desde === hacia) {
    return null;
  }
  return { amount: saldo, from: desde, to: hacia };
}

/** Hay otra persona con acceso al presupuesto (barco compartido). */
export function esBarcoCompartido(
  file: File | null | undefined,
  userId: string | null | undefined,
): boolean {
  return !!file && compartidoCon(file, userId ?? '').length > 0;
}

/** Cuántos caracteres quedan (para el contador bajo la carta). */
export function quedan(textoActual: string, largo = LARGO_CARTA): number {
  return Math.max(0, largo - textoActual.length);
}

/** Firma limpia, o '' si no hay. */
export function firmaLimpia(texto: string): string {
  return texto.trim().slice(0, LARGO_FIRMA);
}

/** El formulario de zarpar ha cambiado la meta (importe, fecha, cadencia). */
export function metaCambiada(
  antes: Objetivo | null | 'otro' | undefined,
  despues: Objetivo,
): boolean {
  if (!antes || antes === 'otro' || antes.tipo !== despues.tipo) {
    return true;
  }
  return JSON.stringify(antes) !== JSON.stringify(despues);
}
